import { gql } from '@apollo/client';

import { CLINICIAN_CHIP_FRAGMENT } from '../../components/clinicians/ClinicianChip.fragments';
import { ClinicianTagsFragment } from '../../components/clinicians/ClinicianTags';
import { ClinicianOptionFragment } from '../../components/clinicians/OptionalClinicianSelector';
import { VisitStateChangeItemFragment } from '../../components/comms/CommsVisitStateChangeItem';
import { DisciplineChipFragment } from '../../components/disciplines/DisciplineChip';
import { DisciplineTagFragment } from '../../components/disciplines/DisciplineTag';
import { DocumentListItemFragment } from '../../components/displayFields/documents';
import { QualificationOptionFragment } from '../../components/formFields/QualificationsField';
import { MatchingCriterionNeed } from '../../components/matchingCriteria/MatchingCriterionChips';
import { OrderChipFragment } from '../../components/orders/OrderChip';
import { OrderStatusFragment } from '../../components/orders/OrderStatus';
import { PatientChipFragment } from '../../components/patients/PatientChip';
import { PatientTagsFragment } from '../../components/patients/PatientTags';
import { SyncStatusFragment } from '../../components/syncing/SyncStatusDisplay';
import { TerritoryOptionFragment } from '../../components/territories/TerritoryAutoCompleteMoreOptions';
import { VisitOrdersListItemFragment } from '../../components/visitOrders/VisitOrdersTable';
import { EditFulfillmentWindowActionFragment } from '../../components/visits/dialogs/EditFulfillmentWindow/useEditFulfillmentWindowAction';
import { EditVisitServicesActionFragment } from '../../components/visits/dialogs/EditVisitServices/EditVisitServicesButton';
import {
    CanRescheduleOrCancelVisitFragment,
    VisitActionsCellFragment,
} from '../../components/visits/tables/VisitActionsCell';
import { ClinicianVisitPerformanceFragment } from '../../pages/ClinicianDetailsPage/ClinicianPerformanceStats';
import { ActiveSnoozeStatusFragment } from '../../pages/DashboardPage/containers/snooze/snoozeStatus';
import {
    SCHEDULE_CLINICIAN_APPOINTMENT_FRAGMENT,
    SCHEDULE_CLINICIAN_FRAGMENT,
    SCHEDULE_CLINICIAN_RESERVATION_FRAGMENT,
    SCHEDULE_SHIFT_BLOCK_FRAGMENT,
    SCHEDULE_SHIFT_FRAGMENT,
    SCHEDULE_TENTATIVE_SHIFT_BLOCK_FRAGMENT,
    SCHEDULE_UNSCHEDULED_VISIT_FRAGMENT,
    SCHEDULE_VISIT_CHIP_FRAGMENT,
    TENTATIVE_CLINICIAN_APPOINTMENT_FRAGMENT,
} from '../../pages/SchedulePage/ClinicianSchedulesTab/ClinicianSchedulesCalendarEvents.fragments';
import { VISIT_AVAILABILITY_FRAGMENT } from '../../pages/VisitBooking/scheduling/BookConfirmed.fragments';
import { EXTERNAL_ID_FRAGMENT } from '../../types/externalId';
import {
    BASIC_ADDRESS_FRAGMENT,
    BASIC_ADDRESS_WITH_COORDINATES_FRAGMENT,
} from '../../utils/addresses';
import { AddressWithLatLngFragment } from '../sharedSchemas/addresses';

/**
 * Common fragments that are registered globally in Apollo Client.
 * These fragments can be used in queries without explicitly importing them.
 *
 * Note: Fragments are still defined co-located with their components/types,
 * but are collected here for registration in the InMemoryCache.
 *
 * Only small, frequently-reused fragments (≤10 fields) are registered here.
 * Large or rarely-used fragments should be imported locally where needed.
 */
export const commonFragments = gql`
    # Address fragments
    ${BASIC_ADDRESS_FRAGMENT}
    ${BASIC_ADDRESS_WITH_COORDINATES_FRAGMENT}
    ${AddressWithLatLngFragment}

    # Clinician fragments
    ${CLINICIAN_CHIP_FRAGMENT}
    ${ClinicianOptionFragment}
    ${ClinicianTagsFragment}
    ${ClinicianVisitPerformanceFragment}

    # Communication fragments
    ${VisitStateChangeItemFragment}

    # Discipline fragments
    ${DisciplineChipFragment}
    ${DisciplineTagFragment}

    # Document fragments
    ${DocumentListItemFragment}

    # External ID fragments
    ${EXTERNAL_ID_FRAGMENT}

    # Matching criteria fragments
    ${MatchingCriterionNeed}

    # Order fragments
    ${OrderChipFragment}
    ${OrderStatusFragment}

    # Patient fragments
    ${PatientChipFragment}
    ${PatientTagsFragment}

    # Qualification fragments
    ${QualificationOptionFragment}

    # Schedule fragments
    ${SCHEDULE_SHIFT_FRAGMENT}
    ${SCHEDULE_SHIFT_BLOCK_FRAGMENT}
    ${SCHEDULE_TENTATIVE_SHIFT_BLOCK_FRAGMENT}
    ${SCHEDULE_CLINICIAN_APPOINTMENT_FRAGMENT}
    ${SCHEDULE_CLINICIAN_RESERVATION_FRAGMENT}
    ${TENTATIVE_CLINICIAN_APPOINTMENT_FRAGMENT}
    ${SCHEDULE_CLINICIAN_FRAGMENT}
    ${SCHEDULE_UNSCHEDULED_VISIT_FRAGMENT}
    ${SCHEDULE_VISIT_CHIP_FRAGMENT}

    # Syncing fragments
    ${SyncStatusFragment}

    # Territory fragments
    ${TerritoryOptionFragment}

    # Visit fragments
    ${CanRescheduleOrCancelVisitFragment}
    ${EditVisitServicesActionFragment}
    ${EditFulfillmentWindowActionFragment}
    ${VisitActionsCellFragment}
    ${VISIT_AVAILABILITY_FRAGMENT}
    ${VisitOrdersListItemFragment}

    # Work queue snooze fragments
    ${ActiveSnoozeStatusFragment}
`;
